import React, { createContext, useContext, useEffect, useMemo } from 'react';

import { FreshTrakLocationsService } from './locations/FreshTrakLocationsService';
import { getLocationsServiceInstance, setLocationsServiceInstance } from './locations/instance';
import { LocationsServiceFacade } from './locations/LocationsServiceFacade';
import { PlentifulLocationsService } from './locations/PlentifulLocationsService';

/**
 * Registry exposed to consumers. React components reach the facade through
 * `useLocationsService()`; Redux thunks reach it through the module-level
 * singleton in ./locations/instance (thunks can't consume React context).
 */
export interface LocationsServiceRegistry {
  locationsService: LocationsServiceFacade;
  isFreshTrakEnabled: boolean;
}

const FALLBACK_FACADE = new LocationsServiceFacade(new PlentifulLocationsService(), null);

const LocationsServiceContext = createContext<LocationsServiceRegistry>({
  locationsService: FALLBACK_FACADE,
  isFreshTrakEnabled: false,
});

export const useLocationsService = (): LocationsServiceRegistry => useContext(LocationsServiceContext);

interface LocationsServiceProviderProps {
  children?: React.ReactNode;
}

/**
 * Builds the LocationsServiceFacade and publishes it via both React context
 * and a module-level singleton so thunks dispatched in the same render cycle
 * as our mount see the facade rather than `null` (which would silently route
 * them down the legacy direct-`Api` fallback path).
 *
 * The FreshTrak overlay is static and bundled, so it is always present in this
 * build — there is no feature gate. (The former Lighthouse overlay was gated
 * on `enable_lighthouse_locations`; that API is offline and no longer wired.)
 */
export const LocationsServiceProvider: React.FC<LocationsServiceProviderProps> = ({ children }) => {
  const registry = useMemo<LocationsServiceRegistry>(() => {
    const plentiful = new PlentifulLocationsService();
    const freshtrak = new FreshTrakLocationsService();
    const facade = new LocationsServiceFacade(plentiful, freshtrak);
    return { locationsService: facade, isFreshTrakEnabled: facade.isFreshTrakEnabled };
  }, []);

  // Intentional in-render side effect: registers the facade synchronously
  // so a thunk dispatched in the same render cycle as our mount sees the
  // active facade. Idempotent (same object reference each render under
  // strict-mode double-renders, and useMemo memoizes the reference).
  setLocationsServiceInstance(registry.locationsService);

  // Cleanup on unmount only — and only if the singleton still points at
  // our facade, so a remount during re-render doesn't blank a freshly
  // installed instance.
  useEffect(
    () => () => {
      if (getLocationsServiceInstance() === registry.locationsService) {
        setLocationsServiceInstance(null);
      }
    },
    [registry]
  );

  return <LocationsServiceContext.Provider value={registry}>{children}</LocationsServiceContext.Provider>;
};

export default LocationsServiceProvider;
